import React, { useState } from "react";
import { Download } from "lucide-react";
import loadingGif from "../../../assets/components/loading.gif";
import api from "../../../services/api";
import formatValue from "../../../utils/formatValue";
import DashboardEmptyState from "../components/DashboardEmptyState";
import DashboardPagination from "../components/DashboardPagination";
import { IPaginationMeta } from "../types/ownerDashboard.types";
import { RentedPropertyCatalog } from "../utils/buildRentedPropertyCatalog";
import {
  buildPropertyListingView,
  formatPropertyInlineLabel,
} from "../utils/rentedPropertyDisplay";

interface IPayment {
  due_date: string;
  tenant: string;
  address: string;
  complement: string;
  value: string;
  status: string;
  payment_id: string;
  kenlo_property_id: string;
}

interface PaymentListProps {
  bills: IPayment[];
  pagination?: IPaginationMeta | null;
  onPageChange?: (page: number) => void;
  rentedPropertyCatalog?: RentedPropertyCatalog;
}

const PAYMENT_LIST_GRID_CLASS =
  "md:grid-cols-[5.75rem_minmax(6.5rem,0.9fr)_minmax(0,2.6fr)_5.5rem_5.25rem_2.5rem] md:gap-2";

function getStatusMeta(status: string) {
  switch (status) {
    case "RECEIVED":
      return { label: "Recebido", className: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" };
    case "SENT":
      return { label: "A receber", className: "bg-white/5 text-[#8b949e] border-white/10" };
    default:
      return { label: "Atrasado", className: "bg-red-500/15 text-red-400 border-red-500/30" };
  }
}

export default function PaymentList({
  bills,
  pagination,
  onPageChange,
  rentedPropertyCatalog,
}: PaymentListProps) {
  const [isDownloadingPayment, setIsDownloadingPayment] = useState({
    isDownloading: false,
    paymentId: null as string | null,
  });

  const totalPages = pagination?.last_page ?? 1;
  const currentPage = pagination?.current_page ?? 1;

  async function handleDownloadPayment(paymentId: string, date: string) {
    setIsDownloadingPayment({ isDownloading: true, paymentId });
    try {
      const response = await api.get(`/api/client/owner/dashboard/payment/${paymentId}`, {
        responseType: "arraybuffer",
      });
      const pdfUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.setAttribute("download", `${date} - ${paymentId} - 7cantos.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.log(err);
    }
    setIsDownloadingPayment({ isDownloading: false, paymentId: null });
  }

  if (bills.length === 0) {
    return (
      <DashboardEmptyState
        align="center"
        title="Nenhum pagamento no período"
        description="Altere o filtro de datas ou aguarde novos repasses para visualizar o histórico aqui."
        className="w-full py-4"
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <div
        className={`hidden w-full px-4 text-[0.65rem] font-semibold uppercase tracking-wider text-[#6e7681] md:grid ${PAYMENT_LIST_GRID_CLASS}`}
      >
        <span>Vencimento</span>
        <span>Inquilino</span>
        <span>Imóvel</span>
        <span>Valor</span>
        <span>Status</span>
        <span className="text-center">PDF</span>
      </div>

      {bills.map((bill) => {
        const value = Number.parseFloat(bill.value);
        const received = bill.status === "RECEIVED";
        const status = getStatusMeta(bill.status);
        const detail = rentedPropertyCatalog?.findByKenloPropertyId(
          bill.kenlo_property_id
        );
        const listing = buildPropertyListingView(detail, {
          address: bill.address,
          complement: bill.complement,
        });
        const rowKey = `${bill.due_date}-${bill.tenant}-${bill.kenlo_property_id}`;
        const propertyLabel = formatPropertyInlineLabel(listing);

        return (
          <div
            key={rowKey}
            className={`w-full rounded-2xl border border-white/[0.08] bg-[#1c2128] p-4 transition-colors hover:border-[#6ea8ff]/25 md:grid md:items-center md:rounded-xl md:px-4 md:py-3 ${PAYMENT_LIST_GRID_CLASS}`}
          >
            <div className="mb-2 flex items-start justify-between gap-2 md:mb-0 md:block">
              <div>
                <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-[#6e7681] md:hidden">
                  Vencimento
                </p>
                <p className="whitespace-nowrap text-sm font-semibold text-[#f0f6fc]">
                  {bill.due_date}
                </p>
              </div>
              <span
                className={`inline-flex shrink-0 rounded-full border px-2.5 py-0.5 text-[0.65rem] font-semibold md:hidden ${status.className}`}
              >
                {status.label}
              </span>
            </div>

            <div className="mb-2 md:mb-0">
              <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-[#6e7681] md:hidden">
                Inquilino
              </p>
              <p className="truncate text-sm text-[#f0f6fc]">{bill.tenant}</p>
            </div>

            <div className="mb-2 md:mb-0">
              <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-[#6e7681] md:hidden">
                Imóvel
              </p>
              <p
                className="truncate text-sm text-[#8b949e]"
                title={propertyLabel}
              >
                {propertyLabel}
              </p>
            </div>

            <div className="mb-2 flex items-center justify-between gap-2 md:mb-0 md:block">
              <div>
                <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-[#6e7681] md:hidden">
                  Valor
                </p>
                <p className="text-sm font-bold text-[#6ea8ff]">{formatValue(value)}</p>
              </div>
              <button
                type="button"
                disabled={!received}
                onClick={() => received && handleDownloadPayment(bill.payment_id, bill.due_date)}
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border md:hidden ${
                  received
                    ? "border-[#6ea8ff]/40 bg-[#6ea8ff]/15 text-[#6ea8ff]"
                    : "cursor-not-allowed border-white/10 bg-white/5 text-[#6e7681]"
                }`}
                aria-label="Baixar boleto"
              >
                {isDownloadingPayment.isDownloading &&
                bill.payment_id === isDownloadingPayment.paymentId ? (
                  <img src={loadingGif} alt="Baixando" className="h-4 w-4" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </button>
            </div>

            <span
              className={`hidden rounded-full border px-2.5 py-1 text-center text-[0.65rem] font-semibold md:inline-flex md:justify-center ${status.className}`}
            >
              {status.label}
            </span>

            <div className="hidden justify-center md:flex">
              <button
                type="button"
                disabled={!received}
                onClick={() => received && handleDownloadPayment(bill.payment_id, bill.due_date)}
                className={`flex h-8 w-8 items-center justify-center rounded-full border ${
                  received
                    ? "border-[#6ea8ff]/40 bg-[#6ea8ff]/15 text-[#6ea8ff] hover:bg-[#6ea8ff]/25"
                    : "cursor-not-allowed border-white/10 bg-white/5 text-[#6e7681]"
                }`}
                aria-label="Baixar boleto"
              >
                {isDownloadingPayment.isDownloading &&
                bill.payment_id === isDownloadingPayment.paymentId ? (
                  <img src={loadingGif} alt="Baixando" className="h-4 w-4" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          </div>
        );
      })}

      {onPageChange && totalPages > 1 && (
        <DashboardPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
